import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { createClient, SupabaseClient, Session } from '@supabase/supabase-js';
import { Database } from '../types/supabase';

// Initialize the Supabase client with environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables in development
if (!supabaseUrl || !supabaseAnonKey) {
  const missingVars = [];
  if (!supabaseUrl) missingVars.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey) missingVars.push('VITE_SUPABASE_ANON_KEY');
  
  console.error(`Missing required environment variables: ${missingVars.join(', ')}`);
  
  // Provide fallbacks for development only
  if (import.meta.env.DEV) {
    console.warn('Using development fallbacks for Supabase configuration');
  }
}

// Safeguard for production - enable persistent sessions
const persistSession = true;

// Default client configuration with retries and error handling
const clientOptions = {
  auth: {
    autoRefreshToken: true,
    persistSession,
    detectSessionInUrl: true
  },
  global: {
    fetch: (...args: [RequestInfo, RequestInit?]) => {
      const [url, options] = args;
      
      // Retry logic for network requests
      const performFetch = async (attempt = 1, maxAttempts = 3): Promise<Response> => {
        try {
          const response = await fetch(url, options);
          
          if (!response.ok && attempt < maxAttempts) {
            // Exponential backoff
            const delay = Math.min(1000 * 2 ** attempt, 10000);
            console.warn(`Supabase request failed (${response.status}), retrying in ${delay}ms...`);
            
            return new Promise(resolve => {
              setTimeout(() => resolve(performFetch(attempt + 1, maxAttempts)), delay);
            });
          }
          
          return response;
        } catch (error) {
          if (attempt < maxAttempts) {
            // Exponential backoff for network errors too
            const delay = Math.min(1000 * 2 ** attempt, 10000);
            console.warn(`Supabase network error, retrying in ${delay}ms...`);
            
            return new Promise(resolve => {
              setTimeout(() => resolve(performFetch(attempt + 1, maxAttempts)), delay);
            });
          }
          
          throw error;
        }
      };
      
      return performFetch();
    }
  }
};

// Create a single supabase client for the entire app
export const supabase = createClient<Database>(
  supabaseUrl, 
  supabaseAnonKey,
  clientOptions
);

// Track first-time initialization to help diagnose deployment issues
let isSupabaseInitialized = false;

type SupabaseContextType = {
  supabase: SupabaseClient<Database>;
  session: Session | null;
};

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

export function SupabaseProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);

  useEffect(() => {
    // Track initialization timing for debugging
    const startTime = Date.now();
    
    if (import.meta.env.PROD) {
      try {
        localStorage.setItem('supabaseInitStart', new Date().toISOString());
      } catch (e) {
        // Ignore storage errors
      }
    }
    
    // Get initial session
    const initializeSupabase = async () => {
      try {
        if (isSupabaseInitialized && import.meta.env.PROD) {
          console.log('Supabase already initialized, skipping duplicate initialization');
          setIsInitialized(true);
          return;
        }
        
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error fetching session:', error);
          setInitializationError(error.message);
        } else {
          setSession(data.session);
          
          // Store session status for recovery mechanisms
          if (import.meta.env.PROD) {
            try {
              sessionStorage.setItem('hasValidSession', data.session ? 'true' : 'false');
            } catch (e) {
              // Ignore storage errors
            }
          }
        }
        
        isSupabaseInitialized = true;
        setIsInitialized(true);
        
        // Log timing for debugging
        const duration = Date.now() - startTime;
        console.log(`Supabase initialization took ${duration}ms`);
        
        if (import.meta.env.PROD) {
          try {
            localStorage.setItem('supabaseInitComplete', new Date().toISOString());
            localStorage.setItem('supabaseInitDuration', duration.toString());
          } catch (e) {
            // Ignore storage errors
          }
        }
      } catch (err) {
        console.error('Failed to initialize Supabase session:', err);
        setInitializationError((err as Error).message);
        setIsInitialized(true); // Still mark as initialized to allow the app to proceed
      }
    };

    initializeSupabase();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      
      // For deployed app diagnostics
      if (import.meta.env.PROD) {
        try {
          sessionStorage.setItem('hasValidSession', session ? 'true' : 'false');
          sessionStorage.setItem('lastSessionUpdate', new Date().toISOString());
        } catch (e) {
          // Ignore storage errors
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Provide a loading state until Supabase is initialized
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full mb-4"></div>
          <p className="text-gray-600">Chargement de l'application...</p>
        </div>
      </div>
    );
  }
  
  // If we had an error initializing, show an error message with retry
  if (initializationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-error-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur de connexion</h2>
          <p className="text-gray-600 mb-6">
            Impossible de se connecter à la base de données. Veuillez vérifier votre connexion internet et réessayer.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Erreur: {initializationError}
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const value = {
    supabase,
    session,
  };

  return <SupabaseContext.Provider value={value}>{children}</SupabaseContext.Provider>;
}

export function useSupabase() {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
}